import { useCallback, useEffect, useRef, useState } from "react";

/** Pixels the pointer must travel before we treat the gesture as a drag, not a tap. */
const DRAG_THRESHOLD_PX = 4;
/** Minimum gap kept between the dragged element and the viewport edges. */
const EDGE_MARGIN_PX = 8;

export interface DraggablePosition {
  x: number;
  y: number;
}

export interface Draggable<T extends HTMLElement> {
  /** Ref to attach to the element that should move. */
  ref: React.RefObject<T | null>;
  /** Spread onto the drag handle (the element itself, or a header). */
  handleProps: {
    onPointerDown: (e: React.PointerEvent) => void;
    onClickCapture: (e: React.MouseEvent) => void;
  };
  /** `null` until the first drag — callers keep their CSS anchoring until then. */
  position: DraggablePosition | null;
  /** True while a drag is in flight (useful to suppress hover/transition styles). */
  dragging: boolean;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Makes a `position: fixed` element draggable by pointer (mouse, touch or pen).
 *
 * Stays inert until the pointer actually moves past {@link DRAG_THRESHOLD_PX}, so the
 * element remains a normal tappable button; the trailing `click` of a real drag is
 * swallowed in the capture phase so dragging never fires the button's action.
 */
export function useDraggable<T extends HTMLElement = HTMLElement>(): Draggable<T> {
  const ref = useRef<T | null>(null);
  const [position, setPosition] = useState<DraggablePosition | null>(null);
  const [dragging, setDragging] = useState(false);

  // Live drag bookkeeping — refs so pointermove never re-subscribes.
  const origin = useRef<{ pointerX: number; pointerY: number; elementX: number; elementY: number } | null>(null);
  const movedRef = useRef(false);

  const clampToViewport = useCallback((x: number, y: number): DraggablePosition => {
    const rect = ref.current?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    const height = rect?.height ?? 0;
    return {
      x: clamp(x, EDGE_MARGIN_PX, Math.max(EDGE_MARGIN_PX, window.innerWidth - width - EDGE_MARGIN_PX)),
      y: clamp(y, EDGE_MARGIN_PX, Math.max(EDGE_MARGIN_PX, window.innerHeight - height - EDGE_MARGIN_PX)),
    };
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Ignore secondary buttons, and let nested controls (close/clear) keep their taps.
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    origin.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      elementX: rect.left,
      elementY: rect.top,
    };
    movedRef.current = false;
  }, []);

  // Move/up live on window so a fast drag that outruns the element keeps tracking.
  useEffect(() => {
    function handleMove(e: PointerEvent) {
      const start = origin.current;
      if (!start) return;

      const dx = e.clientX - start.pointerX;
      const dy = e.clientY - start.pointerY;

      if (!movedRef.current) {
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        movedRef.current = true;
        setDragging(true);
      }

      e.preventDefault();
      setPosition(clampToViewport(start.elementX + dx, start.elementY + dy));
    }

    function handleUp() {
      if (!origin.current) return;
      origin.current = null;
      setDragging(false);
      // movedRef stays true until the click that follows this pointerup is swallowed.
    }

    window.addEventListener("pointermove", handleMove, { passive: false });
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [clampToViewport]);

  // A drag ends with a click event — eat it so dragging never toggles the panel.
  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (!movedRef.current) return;
    movedRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Keep a dragged element on screen when the viewport changes (rotate, keyboard, resize).
  useEffect(() => {
    if (!position) return;
    function handleResize() {
      setPosition((p) => (p ? clampToViewport(p.x, p.y) : p));
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [position, clampToViewport]);

  return { ref, handleProps: { onPointerDown, onClickCapture }, position, dragging };
}
